import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import { paymentAPI, getPaymentStatusColor, formatCurrency } from '../../services/paymentAPI';

interface PaymentStatusProps {
  paymentId: string;
  onStatusUpdate?: (status: string) => void;
  autoRefresh?: boolean;
  refreshInterval?: number; // in seconds
}

interface PaymentStatusData {
  payment_id: string;
  status: string;
  amount: number;
  currency: string;
  payment_method: string;
  updated_at: string;
}

const PaymentStatus: React.FC<PaymentStatusProps> = ({
  paymentId,
  onStatusUpdate,
  autoRefresh = false,
  refreshInterval = 30,
}) => {
  const [status, setStatus] = useState<PaymentStatusData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    fetchPaymentStatus();

    if (autoRefresh) {
      const interval = setInterval(fetchPaymentStatus, refreshInterval * 1000);
      return () => clearInterval(interval);
    }
  }, [paymentId, autoRefresh, refreshInterval]);

  const fetchPaymentStatus = async () => {
    try {
      setError('');
      const response = await paymentAPI.verifyPayment(paymentId);
      setStatus(response.data);
      setLastUpdated(new Date());
      
      if (onStatusUpdate) {
        onStatusUpdate(response.data.status);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch payment status');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (statusValue: string) => {
    switch (statusValue.toLowerCase()) {
      case 'completed':
      case 'captured':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'failed':
      case 'declined':
        return <XCircle className="h-6 w-6 text-red-500" />;
      case 'pending':
      case 'initiated':
        return <Clock className="h-6 w-6 text-yellow-500" />;
      case 'cancelled':
        return <XCircle className="h-6 w-6 text-gray-500" />;
      case 'refunded':
        return <AlertCircle className="h-6 w-6 text-purple-500" />;
      default:
        return <AlertCircle className="h-6 w-6 text-gray-500" />;
    }
  };

  const getStatusMessage = (statusValue: string) => {
    switch (statusValue.toLowerCase()) {
      case 'completed':
      case 'captured':
        return 'Payment completed successfully';
      case 'failed':
      case 'declined':
        return 'Payment failed or was declined';
      case 'pending':
      case 'initiated':
        return 'Payment is being processed';
      case 'cancelled':
        return 'Payment was cancelled';
      case 'refunded':
        return 'Payment has been refunded';
      default:
        return `Payment status: ${statusValue}`;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3" />
          <span className="text-gray-600">Checking payment status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center mb-4">
          <XCircle className="h-6 w-6 text-red-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Error</h3>
        </div>
        <p className="text-red-600 mb-4">{typeof error === 'string' ? error : (error as any)?.message || 'An error occurred'}</p>
        <button
          onClick={fetchPaymentStatus}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="text-center text-gray-500">
          No payment status available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Payment Status</h3>
        <button
          onClick={fetchPaymentStatus}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          title="Refresh status"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-4">
        {/* Status Badge */}
        <div className="flex items-center">
          {getStatusIcon(status.status)}
          <div className="ml-3">
            <div className="flex items-center">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPaymentStatusColor(status.status)}`}>
                {status.status.toUpperCase()}
              </span>
            </div>
            <p className="text-gray-600 text-sm mt-1">
              {getStatusMessage(status.status)}
            </p>
          </div>
        </div>

        {/* Payment Details */}
        <div className="border-t pt-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Payment ID:</span>
              <p className="font-mono text-gray-900 break-all">{status.payment_id}</p>
            </div>
            <div>
              <span className="text-gray-600">Amount:</span>
              <p className="font-semibold text-gray-900">
                {formatCurrency(status.amount, status.currency)}
              </p>
            </div>
            <div>
              <span className="text-gray-600">Payment Method:</span>
              <p className="text-gray-900 capitalize">
                {status.payment_method.replace('_', ' ')}
              </p>
            </div>
            <div>
              <span className="text-gray-600">Last Updated:</span>
              <p className="text-gray-900">
                {new Date(status.updated_at).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Auto-refresh indicator */}
        {autoRefresh && (
          <div className="border-t pt-4">
            <div className="flex items-center text-xs text-gray-500">
              <Clock className="h-3 w-3 mr-1" />
              <span>
                Auto-refreshing every {refreshInterval} seconds
                (Last updated: {lastUpdated.toLocaleTimeString()})
              </span>
            </div>
          </div>
        )}

        {/* Action buttons based on status */}
        {status.status.toLowerCase() === 'pending' && (
          <div className="border-t pt-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <div className="flex items-center">
                <AlertCircle className="h-4 w-4 text-yellow-600 mr-2" />
                <span className="text-yellow-700 text-sm">
                  Payment is being processed. Please wait...
                </span>
              </div>
            </div>
          </div>
        )}

        {status.status.toLowerCase() === 'failed' && (
          <div className="border-t pt-4">
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex items-center">
                <XCircle className="h-4 w-4 text-red-600 mr-2" />
                <span className="text-red-700 text-sm">
                  Payment failed. Please try again or contact support.
                </span>
              </div>
            </div>
          </div>
        )}

        {status.status.toLowerCase() === 'completed' && (
          <div className="border-t pt-4">
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                <span className="text-green-700 text-sm">
                  Payment completed successfully!
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentStatus;